'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { SummaryListingDetails } from '@/src/types/summaryListingDetails.type';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { ListingCard } from '@/src/components/ListingCard';
import { CAROUSEL_CARD_IMAGE_SIZES, ListingCarousel } from '@/src/components/ListingCarousel';
import { Module } from '@/src/components/features/property/ui/Module/Module';
import { BELOW_FOLD_ACTIVATION_ROOT_MARGIN, useViewportActivation } from '@/src/lib/hooks/ui';
import { useRecommendationListings } from './useRecommendationListings';
import {
  getIdentitySet,
  getListingKey,
  hasMatchingIdentity,
  type RecommendationListingsKind,
} from './recommendationListings.utils';
import { carouselItem, container, loadingCard, loadingRow } from './recommendationListingsModule.styles';

type RecommendationModuleConfig = {
  kind: RecommendationListingsKind;
  loadingLabelKey: 'loadingMostPopularListings' | 'loadingPetFriendlyListings';
  moduleId: string;
  titleKey: 'mostPopularListingsTitle' | 'petFriendlyListingsTitle';
};

type RecommendationListingsContentProps = {
  listing: PropertyDetailListing;
  moduleId: string;
  title: string;
  kind: RecommendationListingsKind;
  loadingLabel: string;
};

const RecommendationListingsContent = ({
  listing,
  moduleId,
  title,
  kind,
  loadingLabel,
}: RecommendationListingsContentProps) => {
  // Each carousel adds ~1,600-1,800 DOM elements and ~55-60 imgs while sitting
  // 5,000px+ below the fold; gate the fetch and the card mount on approach.
  const { ref, isActive } = useViewportActivation<HTMLDivElement>({ rootMargin: BELOW_FOLD_ACTIVATION_ROOT_MARGIN });
  const { data, isLoading, error } = useRecommendationListings(listing, kind, { enabled: isActive });

  const recommendations = useMemo(() => {
    const currentListingIds = getIdentitySet(listing);
    const seen = new Set<string>();

    return (data ?? []).filter((recommendation, index) => {
      if (hasMatchingIdentity(recommendation, currentListingIds)) return false;

      const key = getListingKey(recommendation, index);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [data, listing]);

  if (!isActive || isLoading) {
    return (
      <Module title={title} id={moduleId}>
        <div ref={ref} className={loadingRow} role="status" aria-label={loadingLabel} aria-busy="true">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className={loadingCard} />
          ))}
        </div>
      </Module>
    );
  }

  if (error || recommendations.length === 0) {
    return null;
  }

  return (
    <Module title={title} id={moduleId}>
      <div className={container}>
        <ListingCarousel aria-label={title}>
          {recommendations.map((recommendation, index) => (
            <div key={getListingKey(recommendation, index)} className={carouselItem}>
              <ListingCard listing={recommendation as SummaryListingDetails} sizes={CAROUSEL_CARD_IMAGE_SIZES} />
            </div>
          ))}
        </ListingCarousel>
      </div>
    </Module>
  );
};

const RecommendationListingsShell = ({
  listing,
  kind,
  loadingLabelKey,
  moduleId,
  titleKey,
}: RecommendationModuleConfig & { listing: PropertyDetailListing }) => {
  const t = useTranslations('hdp');
  const title = t(titleKey);

  return (
    // Client-side HDP-to-HDP navigation keeps this tree mounted, so key the
    // gated content by listing identity to re-arm the viewport gate.
    <RecommendationListingsContent
      key={listing.aliasEncoded ?? listing.maloneLotIdEncoded}
      listing={listing}
      moduleId={moduleId}
      kind={kind}
      title={title}
      loadingLabel={t(loadingLabelKey)}
    />
  );
};

export const MostPopularListingsModule = ({ listing }: { listing: PropertyDetailListing }) => (
  <RecommendationListingsShell
    listing={listing}
    kind="most-popular"
    loadingLabelKey="loadingMostPopularListings"
    moduleId="most-popular-listings-module"
    titleKey="mostPopularListingsTitle"
  />
);

export const PetFriendlyListingsModule = ({ listing }: { listing: PropertyDetailListing }) => (
  <RecommendationListingsShell
    listing={listing}
    kind="pet-friendly"
    loadingLabelKey="loadingPetFriendlyListings"
    moduleId="pet-friendly-listings-module"
    titleKey="petFriendlyListingsTitle"
  />
);
